import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { ChevronDown, Trash, Menu, Loader, X as XIcon, Info, HelpCircle } from "lucide-react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

// ** UI Components
import { Button } from "@components/common/button"
import { Separator } from "@components/common/separator"
import { Dialog, DialogContent, DialogTitle } from "@components/common/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/common/tabs"
import { Card, CardContent, CardDescription, CardHeader } from "@components/common/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/common/dropdown-menu"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/common/select"

// ** Helpers
import { cn, computeBillTotal, getFinancialErrorMessage } from "@pma/utils/helpers"

// ** Types
import { DictType } from "@pma/types"

// ** Constants
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Stores
import { useAuth } from "@pma/stores/auth-store"

// ** Routes
import {
  ALL_MAIN_BOQ_ENDPOINT,
  BILLS_BY_BOQ_ID__LIST_ENDPOINT,
  DELETE_MAIN_BOQ_ENDPOINT,
  FINANCIAL_SERVER_URL,
  GET_MAIN_BOQ_ENDPOINT,
  PROJECT_BY_ID_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes"
import {
  FINANCIAL_CONTRACTOR_COMPANIES_PAGE_ROUTE,
  PROJECTS_PAGE_ROUTE,
} from "@pma/utils/page-routes"

// ** Utils
import { formatCurrency } from "@pma/utils/utils"

// ** Custom Hooks
import { UserRoleContext } from "@pma/contexts/UserRoleContext"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import UploadBill from "./upload-bill"
import { AllBills } from "./all-bills"
import { PaidBills } from "./paid-bills"
import { BoQDetails } from "./boq-details"
import { SignedBills } from "./signed-bills"
import CreateMainBoQ from "./create-main-boq"
import { UnsignedBills } from "./unsigned-bills"
import { ApprovedAndNotSignedBills } from "./approved-and-not-signed-bills"
import { DeclinedBills } from "./declined-bills"
import { ReturnedBills } from "./contractor-review-bills"
import { EstimateBoQDetails } from "./estimate-boq-details"
import { BillPreviewDialog } from "./bill-template-preview-dialog"
import { FinancialTooltip } from "./financial-glossary"

interface Props {
  boqId: string
  projectId: string
}

const BILL_TAB_COMPONENTS: Record<
  string,
  React.ComponentType<{ boqId: string; mainBoQDetails: any; allBillsOnBoq?: any[] }>
> = {
  all: AllBills,
  paid: PaidBills,
  signed: SignedBills,
  unsigned: UnsignedBills,
  "approved-not-signed": ApprovedAndNotSignedBills,
  declined: DeclinedBills,
  contractor: ReturnedBills,
}

export function BillDetails({ boqId, projectId }: Props) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()
  const { authConfig, setTabManager } = useAuth()

  const { userRoleDetails, setCurrentProjectId } = useContext(UserRoleContext)

  const isHebrew = locale === "he"
  const financial = authConfig?.financial
  const paramBoQId = searchParam.get("main")
  const allStateTabs = authConfig?.tabs ?? {}
  const queryKey = searchParam.get("revalidate")
  const orgId = authConfig?.last_organization_id
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const newParams = new URLSearchParams(searchParam)
  const mainBoQActiveTab = allStateTabs ? allStateTabs["boq"] : undefined
  const otherOptions = [
    "paid",
    "signed",
    "unsigned",
    "declined",
    "contractor",
    "approved-not-signed",
  ]

  const urlBillTab = searchParam.get("bill_tab")

  const defaultTab = urlBillTab
    ? urlBillTab
    : userRoleDetails?.fin_manager
      ? "signed"
      : userRoleDetails?.supervisor
        ? "unsigned"
        : userRoleDetails?.constructor
          ? "contractor"
          : "all"

  const activeTabs: DictType = mainBoQActiveTab || {
    main: "main",
    sub: "bill",
    subChild: defaultTab,
  }

  const showAllBill = !otherOptions.includes(activeTabs?.subChild)

  const [previewMain, setPreviewMain] = useState(false)
  const [createMainBoQ, setCreateMainBoQ] = useState(false)
  const [showProjectInfo, setShowProjectInfo] = useState(true)
  const [uploadBillDialog, setUploadBillDialog] = useState(false)
  const [showPreviewDialog, setShowPreviewDialog] = useState(false)
  const [selectedBoQId, setSelectedBoQId] = useState<string>(boqId)

  const canManageBoQ = userRoleDetails?.supervisor || userRoleDetails?.fin_manager
  const canCreateBill =
    userRoleDetails?.supervisor || userRoleDetails?.constructor || userRoleDetails?.fin_manager

  const getMainBoQs = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + ALL_MAIN_BOQ_ENDPOINT,
      {
        organization_id: orgId,
        project_id: projectId,
        show_deleted: false,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data: mainBoQs,
    refetch: refetchMainBoQs,
    isFetched: isFetchedMain,
  } = useQuery({
    queryKey: ["main-boq", orgId, projectId],
    queryFn: getMainBoQs,
  })

  const activeBoQ = useMemo(() => {
    return mainBoQs?.find((boq: DictType) => boq?.id == boqId)
  }, [boqId, mainBoQs])

  const setActiveBoQId = (boqId: string) => {
    setTabManager({
      group: "boq",
      main: "main",
      sub: activeTabs?.sub || "bills",
      subChild: activeTabs?.subChild || defaultTab,
    })

    const currentParams = new URLSearchParams(window.location.search)
    currentParams.set("main", boqId)

    !!boqId && navigate(`?${currentParams.toString()}`)
  }

  const deleteMainBoQ = async (id: any) => {
    const response = await axios.delete(
      FINANCIAL_SERVER_URL + DELETE_MAIN_BOQ_ENDPOINT + `/${id}`,
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const handleDeleteBoqMutation = useMutation({
    mutationKey: ["delete-boq", boqId],
    mutationFn: deleteMainBoQ,
    onSuccess: () => {
      refetchMainBoQs()
      toast({
        title: translate("Success"),
        description: translate("BoQ Deleted Successfully"),
        variant: "success",
      })
      queryClient.invalidateQueries({ queryKey: ["main-boq"] })
      if (paramBoQId == boqId) {
        newParams.delete("main")

        if (projectId) {
          return navigate(`${PROJECTS_PAGE_ROUTE}/${projectId}`)
        }
      }

      return navigate(`?${newParams.toString()}`)
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "delete bill", translate),
        variant: "destructive",
      })
    },
  })

  useEffect(() => {
    if (queryKey) setShowPreviewDialog(false)
  }, [queryKey])

  useEffect(() => {
    if (urlBillTab) {
      setTabManager({
        group: "boq",
        main: "main",
        sub: "bills",
        subChild: urlBillTab,
      })
    }
  }, [urlBillTab])

  const getMainBoQDetails = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + GET_MAIN_BOQ_ENDPOINT,
      { boq_id: boqId },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const { data: mainBoQDetails } = useQuery({
    queryKey: ["main-boq-details", boqId],
    queryFn: getMainBoQDetails,
  })

  const getAllBillsSummary = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + BILLS_BY_BOQ_ID__LIST_ENDPOINT,
      {
        boq_id: boqId,
        type: "all",
        show_deleted: false,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const { data: billsSummaryData } = useQuery({
    queryKey: ["all-bills-summary", boqId, false],
    queryFn: getAllBillsSummary,
    enabled: !!boqId,
  })

  const billsStats = useMemo(() => {
    const bills = billsSummaryData?.items || []
    const totalBillsCount = bills.length
    const signedBills = bills.filter((b: DictType) => b?.is_signed)
    const signedCount = signedBills.length

    let sumOfAllBills = 0
    let totalPaid = 0

    bills.forEach((bill: DictType) => {
      sumOfAllBills += computeBillTotal(bill, true)
    })

    signedBills.forEach((bill: DictType) => {
      totalPaid += computeBillTotal(bill, false)
    })

    return {
      totalBillsCount,
      signedCount,
      sumOfAllBills,
      totalPaid,
    }
  }, [billsSummaryData])

  const tabCounts = useMemo(() => {
    const bills = billsSummaryData?.items || []
    return {
      all: bills.length,
      contractor: bills.filter((b: DictType) => b?.is_returned_to_contractor).length,
      unsigned: bills.filter((b: DictType) => !b?.is_declined && !b?.is_approved && !b?.is_signed && !b?.is_returned_to_contractor).length,
      signed: bills.filter((b: DictType) => b?.is_approved && !b?.is_signed && !b?.is_declined).length,
      "approved-not-signed": bills.filter((b: DictType) => b?.is_approved && !b?.is_signed).length,
      paid: bills.filter((b: DictType) => b?.is_signed).length,
      declined: bills.filter((b: DictType) => b?.is_declined).length,
    }
  }, [billsSummaryData])

  const tabs = [
    {
      value: "all",
      label: translate("All") + (tabCounts.all > 0 ? ` (${tabCounts.all})` : ""),
      description: translate("tab_desc_all"),
      show: true,
    },
    {
      value: "contractor",
      label: translate("Contractor") + (tabCounts.contractor > 0 ? ` (${tabCounts.contractor})` : ""),
      description: translate("tab_desc_contractor"),
      show:
        userRoleDetails?.constructor || userRoleDetails?.supervisor || userRoleDetails?.fin_manager,
    },
    {
      value: "unsigned",
      label: translate("For Approval") + (tabCounts.unsigned > 0 ? ` (${tabCounts.unsigned})` : ""),
      description: translate("tab_desc_for_approval"),
      show: userRoleDetails?.supervisor || userRoleDetails?.fin_manager,
    },
    {
      value: "signed",
      label: translate("For Signing") + (tabCounts.signed > 0 ? ` (${tabCounts.signed})` : ""),
      description: translate("tab_desc_for_signing"),
      show: userRoleDetails?.fin_manager,
    },
    {
      value: "approved-not-signed",
      label: translate("Approved & Not Signed") + (tabCounts["approved-not-signed"] > 0 ? ` (${tabCounts["approved-not-signed"]})` : ""),
      description: translate("tab_desc_approved_not_signed"),
      show: userRoleDetails?.fin_manager,
    },
    {
      value: "paid",
      label: translate("Signed & Approved") + (tabCounts.paid > 0 ? ` (${tabCounts.paid})` : ""),
      description: translate("tab_desc_signed_approved"),
      show: true,
    },
    {
      value: "declined",
      label: translate("Declined") + (tabCounts.declined > 0 ? ` (${tabCounts.declined})` : ""),
      description: translate("tab_desc_declined"),
      show: true,
    },
  ].filter((tab) => tab.show)

  useEffect(() => {
    if (!projectId && mainBoQDetails) setCurrentProjectId(mainBoQDetails?.boq?.project_id)
  }, [mainBoQDetails])

  const createBoQButtonLabel = financial?.estimate
    ? translate("Generate Main BoQ")
    : translate("Create Main BoQ")

  const renderBoQSelector = () => {
    if (mainBoQs?.length > 0) {
      return (
        <Select value={boqId || undefined} onValueChange={setActiveBoQId}>
          <SelectTrigger
            className={cn(
              "h-8 w-full truncate rounded-md border-none bg-primary text-sm capitalize text-white sm:h-9 sm:w-[160px]",
              isHebrew ? "rtl" : "ltr",
            )}
            aria-label='Select a value'
          >
            <SelectValue
              defaultValue={boqId || undefined}
              className={cn("capitalize", isHebrew ? "rtl" : "ltr")}
              placeholder={translate("Main BoQ")}
            />
          </SelectTrigger>
          <SelectContent
            className={cn("max-w-[90vw] gap-y-2 rounded-xl pb-2", isHebrew ? "rtl" : "ltr")}
          >
            {mainBoQs?.map((boq: DictType, index: number) => (
              <div
                className={cn("flex items-center gap-2 p-0.5", isHebrew ? "rtl" : "ltr")}
                key={index}
              >
                <SelectItem
                  value={String(boq?.id)}
                  className={cn("min-w-0 flex-1 rounded-lg", isHebrew ? "rtl" : "ltr")}
                >
                  <span className='truncate'>{translate(boq?.name)}</span>
                </SelectItem>
                <Button
                  size='icon'
                  variant={"ghost"}
                  disabled={handleDeleteBoqMutation.isPending}
                  loading={selectedBoQId == boq.id ? handleDeleteBoqMutation.isPending : false}
                  onClick={() => {
                    if (!window.confirm(translate("are you sure you want to delete this boq?"))) return
                    setSelectedBoQId(boq.id)
                    handleDeleteBoqMutation.mutate(boq.id)
                  }}
                  className={cn("shrink-0 text-destructive", isHebrew ? "rtl" : "ltr")}
                >
                  <Trash size={14} />
                </Button>
              </div>
            ))}
            {canManageBoQ && (
              <>
                <div className='mt-1'>
                  <Separator />
                </div>
                <div className='flex gap-2'>
                  <Button
                    size={"sm"}
                    variant={"link"}
                    className={cn("text-xs", financial?.estimate && "h-7 py-0")}
                    onClick={() => setCreateMainBoQ(true)}
                  >
                    {createBoQButtonLabel}
                  </Button>
                </div>
              </>
            )}
          </SelectContent>
        </Select>
      )
    }

    if (canManageBoQ) {
      return (
        <Button
          size={"sm"}
          className={cn("text-xs", financial?.estimate && "h-7 py-0")}
          onClick={() => setCreateMainBoQ(true)}
        >
          {createBoQButtonLabel}
        </Button>
      )
    }

    return null
  }

  const ActiveBillComponent = BILL_TAB_COMPONENTS[activeTabs?.subChild]
  const BillContent = showAllBill ? AllBills : ActiveBillComponent

  const boqSum = activeBoQ?.real_sum || 0
  const discountPercent = activeBoQ?.discount || 0
  const totalEstimated = discountPercent > 0 ? boqSum / (1 - discountPercent / 100) : boqSum
  const discountAmount = totalEstimated - boqSum
  const retention = mainBoQDetails?.retention || 0
  const remainingAmount = boqSum - billsStats.totalPaid

  return (
    <Fragment>
      <div className='space-y-4 md:space-y-6'>
        <Card className='w-full space-y-4 overflow-hidden border-none shadow-none md:space-y-6'>
          <CardContent className='p-2 sm:p-4'>
            <Tabs
              defaultValue={activeTabs?.sub}
              className={cn("relative w-full", isHebrew ? "rtl" : "ltr")}
            >
              <div className='flex w-full flex-col gap-3 md:gap-4'>
                {/* Three Cards Row */}
                <div className='grid w-full grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3'>
                  {/* Card 1: BoQ Info */}
                  <Card className='p-3'>
                    <CardHeader className='space-y-3 p-0'>
                      <div className='flex items-center gap-2'>
                        <div className='min-w-0 flex-1'>{renderBoQSelector()}</div>
                        {boqId && (
                          <Button
                            onClick={() => {
                              setPreviewMain(true)
                              setTabManager({
                                group: "boq",
                                main: "main",
                                sub: "boq-tender",
                                subChild: "boq-items",
                              })
                            }}
                            size={"sm"}
                            variant={"link"}
                            className='m-0 hidden whitespace-nowrap p-0 xl:flex'
                          >
                            {translate("Preview BoQ")}
                          </Button>
                        )}
                        {boqId && (
                          <Button
                            onClick={() => setShowProjectInfo(!showProjectInfo)}
                            size={"icon"}
                            variant={"ghost"}
                            className='h-8 w-8 shrink-0'
                            aria-label='Toggle project info'
                          >
                            <ChevronDown
                              className={cn(
                                "transition-transform duration-200",
                                !showProjectInfo && "rotate-180",
                              )}
                            />
                          </Button>
                        )}
                      </div>

                      {showProjectInfo && activeBoQ && (
                        <BoQInfoCard
                          boq={activeBoQ}
                          projectId={projectId}
                          contractorId={activeBoQ?.company_id}
                          totalEstimated={totalEstimated}
                          discountAmount={discountAmount}
                          discountPercent={discountPercent}
                          boqSum={boqSum}
                        />
                      )}
                    </CardHeader>
                  </Card>

                  {/* Card 2: Bill Statistics */}
                  <Card className='p-3'>
                    <CardHeader className='p-0'>
                      <CardDescription className='text-xs sm:text-sm'>
                        <div className='flex flex-col gap-2'>
                          <InfoRow
                            label={translate("Total bills count")}
                            value={String(billsStats.totalBillsCount)}
                          />
                          <InfoRow
                            label={translate("Signed bills count")}
                            value={String(billsStats.signedCount)}
                          />
                          <InfoRow
                            label={translate("Total submitted")}
                            value={formatCurrency(billsStats.sumOfAllBills, isHebrew)}
                          />
                          <InfoRow
                            label={translate("Total paid")}
                            value={formatCurrency(billsStats.totalPaid, isHebrew)}
                          />
                          <div className='flex items-start justify-between gap-2'>
                            <span className='min-w-0 text-muted-foreground'>
                              <FinancialTooltip term="retention">{translate("Accumulated Retention")}</FinancialTooltip>:
                            </span>
                            <span className='shrink-0 text-right font-medium font-mono tabular-nums'>
                              {mainBoQDetails?.retention_percent
                                ? `${mainBoQDetails.retention_percent}% (${formatCurrency(retention, isHebrew)})`
                                : formatCurrency(retention, isHebrew)}
                            </span>
                          </div>
                        </div>
                      </CardDescription>
                    </CardHeader>
                  </Card>

                  {/* Card 3: Remaining Amount + Create Bill */}
                  <Card className='flex flex-col justify-between p-3'>
                    <CardHeader className='p-0'>
                      <CardDescription className='text-xs sm:text-sm'>
                        <div className='flex flex-col gap-2'>
                          <div className='flex items-start justify-between gap-2'>
                            <span className='min-w-0 text-muted-foreground'>
                              <FinancialTooltip term="remaining">{translate("Remaining amount")}</FinancialTooltip>:
                            </span>
                            <span className='shrink-0 text-right font-semibold font-mono tabular-nums text-foreground'>
                              {formatCurrency(remainingAmount, isHebrew)}
                            </span>
                          </div>
                          <p className='text-xs text-muted-foreground'>
                            ({translate("BOQ sum")} - {translate("Total paid")})
                          </p>
                        </div>
                      </CardDescription>
                    </CardHeader>
                    {canCreateBill && (
                      <div className='mt-4'>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size={"sm"} className='w-full shrink-0'>
                              {translate("Create Bill")}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className={cn(isHebrew ? "rtl" : "ltr")}>
                            <DropdownMenuItem
                              className='cursor-pointer hover:!bg-indigo-25 hover:!text-neutral-800'
                              onClick={() => setShowPreviewDialog(true)}
                            >
                              {translate("Create Bill")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className='cursor-pointer hover:!bg-indigo-25 hover:!text-neutral-800'
                              onClick={() => setUploadBillDialog(true)}
                            >
                              {translate("Upload Bill")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </Card>
                </div>

                {/* Tabs */}
                <div className='w-full'>
                  <TabsList className='hidden w-full items-center justify-center bg-transparent md:flex'>
                    {tabs.map((tab) => (
                      <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        className={cn(
                          "whitespace-nowrap rounded-none border-primary bg-transparent data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                          (activeTabs?.subChild === tab.value ||
                            (tab.value === "all" && showAllBill)) &&
                          "border-b-2",
                        )}
                        onClick={() => {
                          setTabManager({
                            group: "boq",
                            main: "main",
                            sub: "bills",
                            subChild: tab.value,
                          })
                        }}
                      >
                        {tab.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  <div className='md:hidden'>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant='outline' className='w-full justify-between'>
                          <span className='truncate'>
                            {tabs.find(
                              (tab) =>
                                activeTabs?.subChild === tab.value ||
                                (tab.value === "all" && showAllBill),
                            )?.label || translate("Select Tab")}
                          </span>
                          <Menu className='ml-2 h-4 w-4 shrink-0' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className='w-[calc(100vw-2rem)] max-w-md'>
                        {tabs.map((tab) => (
                          <DropdownMenuItem
                            key={tab.value}
                            onClick={() => {
                              setTabManager({
                                group: "boq",
                                main: "main",
                                sub: "bills",
                                subChild: tab.value,
                              })
                            }}
                            className={cn(
                              (activeTabs?.subChild === tab.value ||
                                (tab.value === "all" && showAllBill)) &&
                              "bg-accent",
                            )}
                          >
                            {tab.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>

              {/* Active Tab Description */}
              {(() => {
                const activeTab = tabs.find(
                  (tab) =>
                    activeTabs?.subChild === tab.value ||
                    (tab.value === "all" && showAllBill),
                )
                return activeTab?.description ? (
                  <p className='mt-2 text-xs text-muted-foreground'>{activeTab.description}</p>
                ) : null
              })()}

              {/* Role Indicator Badges */}
              <div className='mt-2 flex flex-wrap gap-1.5'>
                {userRoleDetails?.constructor && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{translate("Your role")}: {translate("contractor")}</span>}
                {userRoleDetails?.supervisor && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{translate("Your role")}: {translate("supervisor")}</span>}
                {userRoleDetails?.fin_manager && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{translate("Your role")}: {translate("Financial Manager")}</span>}
                {userRoleDetails?.designer && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{translate("Your role")}: {translate("designer")}</span>}
              </div>

              {/* Role Guidance Banner */}
              <RoleGuidanceBanner userRoleDetails={userRoleDetails} showHelpButton />

              <TabsContent forceMount value={"all"} className='mt-4 p-0'>
                <div className="transition-all duration-200 ease-in-out">
                  {BillContent && <BillContent boqId={boqId} mainBoQDetails={mainBoQDetails} allBillsOnBoq={billsSummaryData?.items || []} />}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>

          {showPreviewDialog && (
            <BillPreviewDialog
              open={showPreviewDialog}
              onOpenChange={setShowPreviewDialog}
              boqId={boqId}
            />
          )}

          <Dialog open={previewMain} onOpenChange={setPreviewMain}>
            <DialogContent className='h-full max-h-[95vh] w-full overflow-y-auto md:max-w-2xl lg:max-w-5xl xl:max-w-7xl'>
              <div className='flex flex-col py-2'>
                <div className='flex w-full justify-between gap-2'>
                  <DialogTitle
                    className={cn("text-base sm:text-lg", isHebrew ? "text-right" : "text-left")}
                  >
                    {translate("Bill of Quantities from tender")}
                  </DialogTitle>
                </div>
              </div>
              <BoQDetails boqId={boqId} closeDialog={() => { }} />
            </DialogContent>
          </Dialog>
        </Card>
      </div>

      {uploadBillDialog && (
        <UploadBill isOpen={uploadBillDialog} setIsOpen={setUploadBillDialog} boqId={boqId} />
      )}

      {createMainBoQ && (
        <CreateMainBoQ
          createMainBoQ={createMainBoQ}
          setCreateMainBoQ={setCreateMainBoQ}
          revalidate={refetchMainBoQs}
          estimateBoQId={financial?.estimate}
          projectId={projectId}
          asLink
        />
      )}
    </Fragment>
  )
}

// -- Shared sub-components --

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <div className='flex items-start justify-between gap-2'>
    <span className='min-w-0 text-muted-foreground'>{label}:</span>
    <span className='shrink-0 text-right font-medium font-mono tabular-nums'>{value}</span>
  </div>
)

interface BoQInfoCardProps {
  boq: DictType
  projectId: string
  contractorId: number
  totalEstimated?: number
  discountAmount?: number
  discountPercent?: number
  boqSum?: number
}

const BoQInfoCard = ({
  boq,
  projectId,
  contractorId,
  totalEstimated,
  discountAmount,
  discountPercent,
  boqSum,
}: BoQInfoCardProps) => {
  const { translate, locale } = useTranslator()
  const { setTabManager } = useAuth()
  const [openBoQDrawer, setOpenBoQDrawer] = useState(false)

  const isHebrew = locale === "he"
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const getProject = async () => {
    const response = await axios.get(SERVER_URL + PROJECT_BY_ID_ENDPOINT + `/${projectId}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    return response.data
  }

  const { data: project, isLoading } = useQuery({
    queryKey: ["get-project", boq?.id, projectId],
    queryFn: getProject,
    enabled: !!projectId,
  })

  return (
    <CardDescription className='text-xs sm:text-sm'>
      <div className='flex flex-col gap-1.5 sm:gap-2'>
        <div className='flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2'>
          <span className='shrink-0 text-muted-foreground'>{translate("Estimate BoQ")}:</span>
          <Button
            onClick={() => {
              setTabManager({
                group: "main-boq",
                main: "estimates",
                sub: undefined,
              })
              setOpenBoQDrawer(true)
            }}
            variant={"link"}
            className='m-0 h-auto justify-start p-0 text-left font-medium'
          >
            <span className='truncate'>{boq?._boq?.name}</span>
          </Button>
        </div>

        <div className='flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2'>
          <span className='shrink-0 text-muted-foreground'>{translate("Project")}:</span>
          {isLoading ? (
            <Loader className='h-4 w-4 animate-spin' />
          ) : (
            <Link
              to={`${PROJECTS_PAGE_ROUTE}/${projectId}?main=${boq?.id}`}
              className='truncate font-medium hover:underline'
            >
              {project?.title || "--"}
            </Link>
          )}
        </div>

        <div className='flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2'>
          <span className='shrink-0 text-muted-foreground'>{translate("Contractor")}:</span>
          <Link
            to={`${FINANCIAL_CONTRACTOR_COMPANIES_PAGE_ROUTE}/${contractorId}`}
            className='truncate font-medium hover:underline'
          >
            {project?._contractor_company?.name}
          </Link>
        </div>

        {totalEstimated != null && (
          <InfoRow
            label={translate("Total Estimated BOQ")}
            value={formatCurrency(totalEstimated, isHebrew)}
          />
        )}

        {discountAmount != null && (
          <div className='flex items-center justify-between gap-2'>
            <span className='shrink-0 text-muted-foreground'>{translate("Discount")}:</span>
            <div className='flex items-center gap-1'>
              <span className='font-medium'>(-) {formatCurrency(discountAmount, isHebrew)}</span>
              <small className='text-muted-foreground'>({discountPercent}%)</small>
            </div>
          </div>
        )}

        {boqSum != null && (
          <InfoRow label={translate("BOQ Sum")} value={formatCurrency(boqSum, isHebrew)} />
        )}
      </div>

      <Dialog open={openBoQDrawer} onOpenChange={setOpenBoQDrawer}>
        <DialogContent
          className={cn(
            "h-full w-full overflow-y-auto md:max-w-2xl lg:max-w-4xl xl:max-w-7xl",
            isHebrew ? "rtl" : "ltr",
          )}
        >
          <div className='flex flex-col'>
            <div className='mb-2 flex w-full gap-2'>
              <DialogTitle
                className={cn("text-base sm:text-lg", isHebrew ? "text-right" : "text-left")}
              >
                {translate("Estimate Bill of Quantities")}
              </DialogTitle>
            </div>
            <EstimateBoQDetails boqId={boq?._boq?.id} closeDialog={() => setOpenBoQDrawer(false)} />
          </div>
        </DialogContent>
      </Dialog>
    </CardDescription>
  )
}

export default BoQInfoCard

// -- Role Guidance Banner --

const GUIDANCE_STORAGE_KEY = "pma_guidance_dismissed"

const ROLE_GUIDANCE: Record<string, string> = {
  constructor: "guidance_contractor_role",
  supervisor: "guidance_supervisor_role",
  fin_manager: "guidance_finance_role",
  designer: "guidance_designer_role",
}

function RoleGuidanceBanner({ userRoleDetails, showHelpButton }: { userRoleDetails: DictType; showHelpButton?: boolean }) {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(GUIDANCE_STORAGE_KEY) === "true"
    } catch {
      return false
    }
  })

  const handleDismiss = useCallback(() => {
    setDismissed(true)
    try {
      localStorage.setItem(GUIDANCE_STORAGE_KEY, "true")
    } catch {
      // localStorage unavailable
    }
  }, [])

  const handleReshow = useCallback(() => {
    setDismissed(false)
    try {
      localStorage.removeItem(GUIDANCE_STORAGE_KEY)
    } catch {
      // localStorage unavailable
    }
  }, [])

  // Collect ALL active roles for the user
  const activeRoleKeys = (["constructor", "supervisor", "fin_manager", "designer"] as const).filter(
    (key) => userRoleDetails?.[key]
  )

  if (activeRoleKeys.length === 0) return null

  if (dismissed) {
    return showHelpButton ? (
      <div className='mt-2 flex justify-end'>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={handleReshow}
          aria-label={translate("Show guidance")}
        >
          <HelpCircle className="h-4 w-4" />
        </Button>
      </div>
    ) : null
  }

  return (
    <div
      className={cn(
        "mt-3 flex flex-col gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm",
        isHebrew ? "rtl" : "ltr",
      )}
      dir={isHebrew ? "rtl" : "ltr"}
    >
      {activeRoleKeys.map((roleKey) => (
        <div key={roleKey} className='flex items-start gap-2'>
          <Info className='mt-0.5 h-4 w-4 shrink-0 text-blue-500' />
          <p className='flex-1 text-blue-700'>{translate(ROLE_GUIDANCE[roleKey])}</p>
        </div>
      ))}
      <button
        onClick={handleDismiss}
        className='self-end shrink-0 rounded p-0.5 text-blue-400 hover:bg-blue-100 hover:text-blue-600'
        aria-label='Dismiss'
      >
        <XIcon className='h-4 w-4' />
      </button>
    </div>
  )
}
